import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Building2, Users, Eye, MoreHorizontal, Trash2, Mail, Zap, Shield, Crown, MapPin, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { getTierLimits, TIER_NAMES, formatCurrency, SubscriptionTier } from '@/lib/subscriptionFeatures';
import { Separator } from '@/components/ui/separator';

interface Company {
  id: string;
  company_name: string;
  slug: string;
  subscription_tier: string;
  subscription_status: string;
  max_users: number;
  max_sites: number | null;
  ai_pool_limit: number | null;
  ai_pool_used: number | null;
  trial_ends_at: string | null;
  created_at: string;
  settings?: {
    industry_sector: string;
    contact_email: string;
    contact_phone: string;
    active_users_count: number;
    mrr_arr: number;
  } | null;
}

interface CompanyAdmin {
  id: string;
  full_name: string;
  user_id: string;
}

interface CompanySite {
  id: string;
  location_name: string;
  is_active: boolean;
}

const SuperAdminClients = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([]);
  const [companySites, setCompanySites] = useState<CompanySite[]>([]);
  const [newCompany, setNewCompany] = useState({
    company_name: '',
    slug: '',
    industry_sector: 'manufacturing',
    subscription_tier: 'free' as SubscriptionTier,
    max_users: 50,
    admin_email: '',
    admin_password: '',
    admin_name: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setIsLoading(true);
    try {
      // Fetch companies with their settings
      const { data: companiesData, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch settings for each company
      const companiesWithSettings = await Promise.all(
        (companiesData || []).map(async (company) => {
          const { data: settings } = await supabase
            .from('company_settings')
            .select('industry_sector, contact_email, contact_phone, active_users_count, mrr_arr')
            .eq('company_id', company.id)
            .maybeSingle();
          
          return { ...company, settings };
        })
      );

      setCompanies(companiesWithSettings);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanyDetails = async (company: Company) => {
    // Fetch admins for this company
    const { data: admins } = await supabase
      .from('worker_profiles')
      .select('id, full_name, user_id')
      .eq('company_id', company.id)
      .in('role', ['admin', 'Admin']);
    
    setCompanyAdmins(admins || []);

    // Fetch sites for this company
    const { data: sites } = await supabase
      .from('company_locations')
      .select('id, location_name, is_active')
      .eq('company_id', company.id);
    
    setCompanySites(sites || []);
  };

  const handleViewDetails = async (company: Company) => {
    setSelectedCompany(company);
    await fetchCompanyDetails(company);
    setIsDetailDialogOpen(true);
  };

  const handleContact = (company: Company) => {
    const email = company.settings?.contact_email;
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      toast({
        title: "No Contact Email",
        description: "This company doesn't have a contact email configured.",
        variant: "destructive",
      });
    }
  };

  const handleSuspend = async (company: Company) => {
    if (!confirm(`Are you sure you want to suspend ${company.company_name}? Users will lose access.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('companies')
        .update({ subscription_status: 'suspended' })
        .eq('id', company.id);

      if (error) throw error;

      toast({
        title: "Company Suspended",
        description: `${company.company_name} has been suspended.`,
      });

      fetchCompanies();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleReactivate = async (company: Company) => {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ subscription_status: 'active' })
        .eq('id', company.id);

      if (error) throw error;

      toast({
        title: "Company Reactivated",
        description: `${company.company_name} is now active.`,
      });

      fetchCompanies();
      setIsDetailDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleAddCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const slug = newCompany.slug || generateSlug(newCompany.company_name);
      const tierLimits = getTierLimits(newCompany.subscription_tier);

      // 1. Create the company with proper tier limits
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert({
          company_name: newCompany.company_name,
          slug,
          subscription_tier: newCompany.subscription_tier,
          subscription_status: 'active', // Always active, no more 'trial' status
          max_users: newCompany.max_users,
          ai_pool_limit: tierLimits.aiConversationsPerMonth,
          max_sites: tierLimits.maxSites,
          ai_pool_used: 0,
          topup_count_this_month: 0,
          topup_conversations: 0,
        })
        .select()
        .single();

      if (companyError) throw companyError;

      // 2. Create company settings with matching tier value
      const { error: settingsError } = await supabase
        .from('company_settings')
        .insert({
          company_id: companyData.id,
          company_name: newCompany.company_name,
          industry_sector: newCompany.industry_sector,
          contact_email: newCompany.admin_email,
          subscription_level: newCompany.subscription_tier, // Use same tier value
          account_status: 'active',
        });

      if (settingsError) throw settingsError;

      // 3. Create default site
      const { error: siteError } = await supabase
        .from('company_locations')
        .insert({
          company_id: companyData.id,
          location_name: 'Kantor Utama',
          description: 'Lokasi utama perusahaan',
          is_active: true,
          subscription_tier: newCompany.subscription_tier,
          subscription_status: 'active',
        });

      if (siteError) {
        console.error('Failed to create default site:', siteError);
      }

      // 4. Create admin user via edge function
      if (newCompany.admin_email && newCompany.admin_password) {
        const response = await supabase.functions.invoke('create-user', {
          body: {
            email: newCompany.admin_email,
            password: newCompany.admin_password,
            fullName: newCompany.admin_name || 'Company Admin',
            displayName: newCompany.admin_name || 'Company Admin',
            team: 'Management',
            role: 'admin',
            companyId: companyData.id,
          },
        });

        if (response.error || (response.data && !response.data.success)) {
          console.error('Error creating admin user:', response.error || response.data?.error);
          toast({
            title: "Warning",
            description: `Company created but admin user creation failed: ${response.data?.error || response.error?.message}. You can add admin users later.`,
            variant: "destructive",
          });
        } else {
          console.log('Admin user created successfully:', response.data);
        }
      }

      toast({
        title: "Success",
        description: `Company "${newCompany.company_name}" created successfully with ${TIER_NAMES[newCompany.subscription_tier]} plan`,
      });

      setIsDialogOpen(false);
      setNewCompany({
        company_name: '',
        slug: '',
        industry_sector: 'manufacturing',
        subscription_tier: 'free',
        max_users: 50,
        admin_email: '',
        admin_password: '',
        admin_name: '',
      });
      fetchCompanies();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      active: 'default',
      inactive: 'outline',
      suspended: 'destructive',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const getTierBadge = (tier: string) => {
    switch (tier) {
      case 'professional':
        return <Badge className="bg-primary text-primary-foreground"><Zap className="h-3 w-3 mr-1" />Professional</Badge>;
      case 'enterprise':
        return <Badge className="bg-success text-success-foreground"><Shield className="h-3 w-3 mr-1" />Enterprise</Badge>;
      case 'enterprise_plus':
        return <Badge className="bg-warning text-warning-foreground"><Crown className="h-3 w-3 mr-1" />Enterprise+</Badge>;
      default:
        return <Badge variant="outline">Free</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Client Companies
              </CardTitle>
              <CardDescription>
                Manage all client companies on the SORIN platform
              </CardDescription>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Company
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Client Company</DialogTitle>
                  <DialogDescription>Create a new client company with an admin account</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddCompany} className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground">Company Details</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name *</Label>
                        <Input
                          id="company_name"
                          value={newCompany.company_name}
                          onChange={(e) => setNewCompany({ 
                            ...newCompany, 
                            company_name: e.target.value,
                            slug: generateSlug(e.target.value)
                          })}
                          placeholder="Acme Corporation"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slug">URL Slug</Label>
                        <Input
                          id="slug"
                          value={newCompany.slug}
                          onChange={(e) => setNewCompany({ ...newCompany, slug: e.target.value })}
                          placeholder="acme-corp"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="industry_sector">Industry</Label>
                        <Select
                          value={newCompany.industry_sector}
                          onValueChange={(value) => setNewCompany({ ...newCompany, industry_sector: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manufacturing">Manufacturing</SelectItem>
                            <SelectItem value="construction">Construction</SelectItem>
                            <SelectItem value="oil_gas">Oil & Gas</SelectItem>
                            <SelectItem value="mining">Mining</SelectItem>
                            <SelectItem value="logistics">Logistics</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subscription_tier">Subscription Tier</Label>
                        <Select
                          value={newCompany.subscription_tier}
                          onValueChange={(value) => setNewCompany({ ...newCompany, subscription_tier: value as SubscriptionTier })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free - Rp 0/bulan</SelectItem>
                            <SelectItem value="professional">Professional - {formatCurrency(18000000)}/bulan</SelectItem>
                            <SelectItem value="enterprise">Enterprise - {formatCurrency(35000000)}/bulan</SelectItem>
                            <SelectItem value="enterprise_plus">Enterprise+ - Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="max_users">Max Users</Label>
                      <Input
                        id="max_users"
                        type="number"
                        value={newCompany.max_users}
                        onChange={(e) => setNewCompany({ ...newCompany, max_users: parseInt(e.target.value) || 50 })}
                        min={1}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground">Admin Account (Optional)</h4>
                    <p className="text-xs text-muted-foreground">Create an admin user for this company. You can also add admins later.</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="admin_name">Admin Name</Label>
                        <Input
                          id="admin_name"
                          value={newCompany.admin_name}
                          onChange={(e) => setNewCompany({ ...newCompany, admin_name: e.target.value })}
                          placeholder="John Smith"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin_email">Admin Email</Label>
                        <Input
                          id="admin_email"
                          type="email"
                          value={newCompany.admin_email}
                          onChange={(e) => setNewCompany({ ...newCompany, admin_email: e.target.value })}
                          placeholder="admin@company.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="admin_password">Admin Password</Label>
                      <Input
                        id="admin_password"
                        type="password"
                        value={newCompany.admin_password}
                        onChange={(e) => setNewCompany({ ...newCompany, admin_password: e.target.value })}
                        placeholder="Minimum 6 characters"
                        minLength={6}
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Creating..." : "Create Company"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>MRR</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">Loading...</TableCell>
                  </TableRow>
                ) : companies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No companies found. Add your first client company to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{company.company_name}</div>
                          <div className="text-xs text-muted-foreground">{company.slug}</div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">
                        {company.settings?.industry_sector?.replace('_', ' ') || 'N/A'}
                      </TableCell>
                      <TableCell>{getTierBadge(company.subscription_tier || 'free')}</TableCell>
                      <TableCell>{getStatusBadge(company.subscription_status || 'active')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          {company.settings?.active_users_count || 0} / {company.max_users}
                        </div>
                      </TableCell>
                      <TableCell>${(company.settings?.mrr_arr || 0).toLocaleString()}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {new Date(company.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetails(company)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleContact(company)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Contact
                            </DropdownMenuItem>
                            {company.subscription_status === 'suspended' ? (
                              <DropdownMenuItem onClick={() => handleReactivate(company)}>
                                <Zap className="h-4 w-4 mr-2" />
                                Reactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleSuspend(company)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Company Details Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Company Details</DialogTitle>
            <DialogDescription>
              {selectedCompany?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              {/* Company Info */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground capitalize">
                  {selectedCompany.settings?.industry_sector?.replace('_', ' ') || 'N/A'}
                </span>
                <span className="text-muted-foreground">•</span>
                {getTierBadge(selectedCompany.subscription_tier || 'free')}
                <span className="text-muted-foreground">•</span>
                {getStatusBadge(selectedCompany.subscription_status || 'active')}
              </div>

              <Separator />

              {/* Contact Information */}
              <div>
                <h4 className="font-medium mb-2">Contact Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedCompany.settings?.contact_email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Phone:</span>
                    <span>{selectedCompany.settings?.contact_phone || '-'}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Usage Statistics */}
              <div>
                <h4 className="font-medium mb-2">Usage</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-muted rounded-lg p-3">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Users className="h-4 w-4" />
                      <span>Active Users</span>
                    </div>
                    <div className="font-semibold">
                      {selectedCompany.settings?.active_users_count || 0} / {selectedCompany.max_users || '∞'}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <MessageSquare className="h-4 w-4" />
                      <span>AI Conversations</span>
                    </div>
                    <div className="font-semibold">
                      {selectedCompany.ai_pool_used || 0} / {selectedCompany.ai_pool_limit === -1 ? '∞' : selectedCompany.ai_pool_limit || 0}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <MapPin className="h-4 w-4" />
                      <span>Sites</span>
                    </div>
                    <div className="font-semibold">
                      {companySites.length} / {selectedCompany.max_sites === -1 ? '∞' : selectedCompany.max_sites || 1}
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Admins */}
              <div>
                <h4 className="font-medium mb-2">Admins</h4>
                {companyAdmins.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No admins assigned yet</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {companyAdmins.map((admin) => (
                      <Badge key={admin.id} variant="secondary">
                        {admin.full_name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Sites */}
              <div>
                <h4 className="font-medium mb-2">Sites</h4>
                {companySites.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No sites configured</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {companySites.map((site) => (
                      <Badge 
                        key={site.id} 
                        variant={site.is_active ? "outline" : "secondary"}
                      >
                        <MapPin className="h-3 w-3 mr-1" />
                        {site.location_name}
                        {!site.is_active && " (inactive)"}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedCompany?.subscription_status === 'suspended' ? (
              <Button onClick={() => selectedCompany && handleReactivate(selectedCompany)}>
                <Zap className="h-4 w-4 mr-2" />
                Reactivate Company
              </Button>
            ) : (
              <Button 
                variant="destructive" 
                onClick={() => {
                  if (selectedCompany) {
                    handleSuspend(selectedCompany);
                    setIsDetailDialogOpen(false);
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Suspend Company
              </Button>
            )}
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminClients;
